import React, { useMemo, useState } from "react";
import styled from "styled-components";

import {
  NoResults,
  useQueryGetCourseWithUnits,
  PrintYagModal,
  useModal,
  useAppContext,
} from "@examspark/common-app";
import {
  CheckboxInput,
  FilterChips,
  Spinner,
  Text,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import { IconDocumentLine } from "@examspark/common-ui/dist/components/icons";
import { FilterChip } from "@examspark/common-ui/dist/components/inputs/FilterChips";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import {
  TeksCategoryColors,
  YagDataSource,
  getObjectiveCategory,
  getObjectiveCurriculumUsage,
  calculateYag,
} from "@examspark/customer/tcmpc";
import {
  Classroom,
  Course,
  TeksCategory,
  TeksCategoryList,
  TeksCategoryMeta,
  TeksCurriculumUsage,
} from "@examspark/types";

import { useFlags } from "../../../hooks/useFlags";
import { useUsername } from "../../../hooks/useUsername";
import { WindowContext } from "../../common/AppWrapper";
import { StyledCheckboxLabel } from "../../common/Objectives";
import PageMenu from "../../common/PageMenu";
import UnitContent from "../../common/UnitContent";
import IconLeaf from "../../icons/IconLeaf";
import IconSun from "../../icons/IconSun";

import YagPeriod from "./yag/YagPeriod";
import YagSelect from "./yag/YagSelect";

const Styled = styled.div`
  display: flex;
  gap: 2rem;
`;
const Semester = styled.div`
  flex: 1;
  border: solid 1px #f0f0f0;
  background-color: ${colorFromTheme("surface1")};
  border-radius: 0.5rem;
`;
const SemesterTitle = styled.div`
  font-size: 1.8rem;
  color: #333;
  padding: 1.5rem 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.7rem;
  font-weight: 300;
`;
const SemesterBody = styled.div`
  /* padding: 1rem; */
`;
const StyledPageMenuWrapper = styled.div`
  margin-left: auto;
`;

export type ClassroomPlannerYagProps = {
  course: Course;
  value: YagDataSource;
  onChange?: (cal: YagDataSource) => void;
  onGroupChange?: (showStandardsInGroups: boolean) => void;
  linkContext:
    | {
        type: "classroom";
        classroom: Classroom;
      }
    | {
        type: "course";
        course: Course;
      };
};

export type TeksFilter = TeksCategory | TeksCurriculumUsage | "NO_CATEGORY";

const ClassroomPlannerYag = ({
  value,
  onChange,
  onGroupChange,
  linkContext,
  ...props
}: ClassroomPlannerYagProps) => {
  const flags = useFlags();
  const printYagModal = useModal();
  const win = React.useContext(WindowContext);
  const username = useUsername();
  const [showStandardsInGroups, setShowStandardsInGroups] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState<TeksFilter[]>([
    "PROCESS",
    "READINESS",
    "SUPPORTING",
    "RTC",
    "SEP",
    "NO_CATEGORY",
    "DIRECTLY_TAUGHT",
  ]);

  const { data: course } = useQueryGetCourseWithUnits(props.course.id);

  let calendarId: string | undefined;
  let weeksCount: number | undefined;

  if (value?.type === "CourseCalendar") {
    calendarId = value.value.calendarId;
  } else if (value?.type === "UnitLayout") {
    weeksCount = 36 / (value.value.buckets?.length ?? 36);
  }

  const yag = useMemo(() => {
    return calculateYag(course?.units ?? [], value);
  }, [course?.units, value]);

  const existingCategories = useMemo(
    () =>
      new Set<TeksCategory | "NO_CATEGORY">(
        yag.gradingPeriods
          .flatMap((gp) => gp.units)
          .flatMap((u) => u.unit.objectives ?? [])
          .filter((uo) => getObjectiveCurriculumUsage(uo) === "DIRECTLY_TAUGHT")
          .map((uo) => getObjectiveCategory(uo?.objective) ?? "NO_CATEGORY"),
      ),
    [yag.gradingPeriods],
  );

  // default to the workspace default calendar if they did not pass in a value
  const [state] = useAppContext();

  const labelOverrides: Partial<Record<TeksCategory, string>> = {
    RTC: "RTCs",
    SEP: "SEPs",
  };

  if (course == null) {
    return <NoResults icon={<Spinner />} />;
  }

  return (
    <>
      {state.workspace && (
        <PrintYagModal
          title="Download YAG as PDF"
          courseId={props.course.id}
          calendarId={calendarId}
          weeks={weeksCount?.toString()}
          showStandardsInGroups={showStandardsInGroups}
          workspace={state.workspace.name ?? null}
          workspaceId={state.workspace.id}
          username={username}
          {...printYagModal.modalProps}
        />
      )}
      <VSpace size="1.5rem">
        {onChange && (
          <VSpace size="1rem">
            <Text.H4>Active Calendar</Text.H4>
            <YagSelect
              course={course}
              value={value}
              onChange={onChange}
              workspaceId={state.workspace?.id}
              allowTypes={["UnitLayout"]}
            />
          </VSpace>
        )}
        {value != null && (
          <>
            <VSpace size="1rem">
              <UnitContent content={value.value.description} />
              <Styled>
                <Text.H4>Filter Standards</Text.H4>
                <StyledPageMenuWrapper>
                  <PageMenu
                    buttons={[
                      flags.printCourseYagEnabled && {
                        title: "Download as PDF",
                        icon: <IconDocumentLine />,
                        onClick: () => printYagModal.open(),
                      },
                    ]}
                  />
                </StyledPageMenuWrapper>
              </Styled>
              <Toolbar>
                <FilterChips
                  value={selectedFilters}
                  onChange={(values) => {
                    setSelectedFilters(values as TeksFilter[]);
                  }}
                >
                  {TeksCategoryList.filter((c) =>
                    existingCategories.has(c),
                  ).map((category) => (
                    <FilterChip
                      key={category}
                      value={category}
                      color={TeksCategoryColors[category]}
                    >
                      {labelOverrides[category] ??
                        TeksCategoryMeta[category].label}
                    </FilterChip>
                  ))}
                  {existingCategories.has("NO_CATEGORY") && (
                    <FilterChip value="NO_CATEGORY" color="#888">
                      Other
                    </FilterChip>
                  )}
                </FilterChips>
                <StyledCheckboxLabel label="Show In Groups" layout="row">
                  <CheckboxInput
                    checked={showStandardsInGroups}
                    onChange={({ checked }) => {
                      setShowStandardsInGroups(checked);
                      onGroupChange?.(checked);
                    }}
                  />
                </StyledCheckboxLabel>
              </Toolbar>
            </VSpace>
            <Styled
              style={{
                flexDirection: win.device === "phone" ? "column" : "row",
              }}
            >
              <Semester>
                <SemesterTitle>
                  <IconLeaf fill="#1E751D" width="24" />
                  <div>First Semester</div>
                </SemesterTitle>
                <SemesterBody>
                  {yag.gradingPeriods
                    .filter((gp) => gp.semesterIndex === 1)
                    .map((gp) => (
                      <YagPeriod
                        key={gp.name}
                        linkContext={linkContext}
                        course={course}
                        data={gp}
                        listStyle={win.device === "phone" ? "list" : "grid"}
                        filters={selectedFilters}
                        showStandardsInGroups={showStandardsInGroups}
                      />
                    ))}
                </SemesterBody>
              </Semester>
              <Semester>
                <SemesterTitle>
                  <IconSun fill="#B8BA4C" width="24" />
                  <div>Second Semester</div>
                </SemesterTitle>
                <SemesterBody>
                  {yag.gradingPeriods
                    .filter((gp) => gp.semesterIndex === 2)
                    .map((gp) => (
                      <YagPeriod
                        key={gp.name}
                        linkContext={linkContext}
                        course={course}
                        data={gp}
                        listStyle={win.device === "phone" ? "list" : "grid"}
                        filters={selectedFilters}
                        showStandardsInGroups={showStandardsInGroups}
                      />
                    ))}
                </SemesterBody>
              </Semester>
            </Styled>
          </>
        )}
      </VSpace>
    </>
  );
};

export default ClassroomPlannerYag;
